import { useState, useEffect } from 'react';
import { costCenterApi } from '@/api/costCenter.api';
import { cn } from '@/utils/cn';

interface CostCentreSelectProps {
  value?: string;
  onChange: (costCentreId: string) => void;
  disabled?: boolean;
  className?: string;
}

interface CostCentreOption { id: string; code: string; name: string; }

export const CostCentreSelect = ({ value = '', onChange, disabled, className }: CostCentreSelectProps) => {
  const [options, setOptions]   = useState<CostCentreOption[]>([]);
  const [loading, setLoading]   = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    setLoading(true);
    setHasError(false);
    costCenterApi.getList({ pageSize: 200 })
      .then(res => {
        const items = res.data?.data?.items ?? [];
        setOptions(
          items.map((c: { id: string; code: string; name: string }) => ({
            id: c.id, code: c.code, name: c.name,
          }))
        );
      })
      .catch(err => {
        console.error('CostCentreSelect: failed to load cost centres', err);
        setHasError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  const selectCls = cn(
    'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-sm',
    'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
    'focus:outline-none focus:ring-2 focus:ring-brand-500',
    (disabled || loading) && 'opacity-60 cursor-not-allowed',
    className
  );

  if (hasError) {
    return (
      <select disabled className={selectCls}>
        <option>(Error loading cost centres)</option>
      </select>
    );
  }

  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled || loading}
      className={selectCls}
    >
      <option value="">
        {loading ? 'Loading cost centres…' : 'All Cost Centres'}
      </option>
      {options.map(o => (
        <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
      ))}
    </select>
  );
};
